import { eq } from "drizzle-orm";
import { dining_table, type PowerSyncDrizzleDB, reservation } from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

export type ReservationKind = "reservation" | "waitlist";
export type ReservationStatus = "reserved" | "seated" | "no_show" | "cancelled";

export interface CreateReservationOpts {
  tableId?: string | null;
  customerId?: string | null;
  phone?: string;
  partySize?: number;
  /** ISO-8601. NULL para waitlist (sin hora comprometida). */
  reservedAt?: string | null;
  kind?: ReservationKind;
  note?: string;
}

/** Crea una reserva (o entrada de waitlist). CC-7: escritura con await. */
export async function createReservationMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
  customerName: string,
  opts: CreateReservationOpts = {},
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(reservation).values({
    id,
    organization_id: organizationId,
    branch_id: branchId,
    table_id: opts.tableId ?? null,
    customer_id: opts.customerId ?? null,
    customer_name: customerName.trim(),
    phone: opts.phone?.trim() || null,
    party_size: Math.max(1, Math.trunc(opts.partySize ?? 2)),
    reserved_at: opts.reservedAt ?? null,
    kind: opts.kind ?? "reservation",
    status: "reserved",
    note: opts.note?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Cambia el estado de una reserva (reserved → seated/no_show/cancelled). */
export async function setReservationStatusMutation(
  db: PowerSyncDrizzleDB,
  reservationId: string,
  status: ReservationStatus,
): Promise<void> {
  await db
    .update(reservation)
    .set({ status, updated_at: nowISO() })
    .where(eq(reservation.id, reservationId));
}

/**
 * Sienta una reserva: la marca `seated` y, si tiene mesa asignada, la pone
 * `occupied`. CC-7: ambas escrituras con await.
 */
export async function seatReservationMutation(
  db: PowerSyncDrizzleDB,
  reservationId: string,
  tableId?: string | null,
): Promise<void> {
  const now = nowISO();
  await db
    .update(reservation)
    .set({
      status: "seated",
      ...(tableId ? { table_id: tableId } : {}),
      updated_at: now,
    })
    .where(eq(reservation.id, reservationId));

  const current = await db
    .select({ table_id: reservation.table_id })
    .from(reservation)
    .where(eq(reservation.id, reservationId))
    .limit(1);
  const finalTableId = tableId ?? current[0]?.table_id ?? null;
  if (finalTableId) {
    await db
      .update(dining_table)
      .set({ status: "occupied", updated_at: now })
      .where(eq(dining_table.id, finalTableId));
  }
}

export async function deleteReservationMutation(
  db: PowerSyncDrizzleDB,
  reservationId: string,
): Promise<void> {
  await db.delete(reservation).where(eq(reservation.id, reservationId));
}
